import {
  Drawer,
  DrawerBody,
  DrawerHeader,
} from '@one-thd/sui-atomic-components';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import React, { useState } from 'react';
import { FeatureSwitches } from '../../../constants/feature-switches';
import { usePurchaseHistoryContext } from '../../../hooks/useContexts';
import { UserFilterSettings } from '../../../models/order-filter';
import {
  IPHFilter,
  IPHRangeFilter,
  IPHRequest,
  IPHValuesFilter,
} from '../../../models/purchase-history';
import { setPHFilterToSession } from '../../../utils/purchase-history/order-filter-utils';

import { OrderFilterGroup } from './OrderFilterGroup';
import { OrderFilterFooter } from './OrderFilterFooter';
import { OrderFilterUserSection } from './OrderFilterUserSection';
import { pushEventInfo } from '../../../utils/analytics';
import { OrderTotalAmountFilter } from './OrderTotalAmountFilter';

interface IOrderFilterDrawer {
  isDrawerOpen: boolean;
  setIsDrawerOpen: (isOpen: boolean) => void;
  dateFilterDot: {
    date: boolean;
    user: boolean;
  };
  setDateFilterDot: React.Dispatch<
    React.SetStateAction<{
      date: boolean;
      user: boolean;
    }>
  >;
  permissionRulesCheck: boolean;
}

export const OrderFilterDrawer = ({
  isDrawerOpen,
  setIsDrawerOpen,
  dateFilterDot,
  setDateFilterDot,
  permissionRulesCheck,
}: IOrderFilterDrawer) => {
  const { username, userID } = useThdCustomer();

  const { pHRequest, setPHRequest, poJobFilters, paymentFilters, userFilters } =
    usePurchaseHistoryContext();

  const poJobNameTileOptions = poJobFilters.map((filter) => ({
    label: filter,
    value: filter,
  }));

  const paymentTileOptions = paymentFilters.map((filter) => ({
    label: filter.last4,
    value: filter.last4,
  }));

  const currentUserFilterIndex = userFilters.findIndex(
    (user) => user.userId === userID,
  );
  const orderedUserFilters =
    currentUserFilterIndex > 0
      ? [
          userFilters[currentUserFilterIndex],
          ...userFilters.slice(0, currentUserFilterIndex),
          ...userFilters.slice(currentUserFilterIndex + 1),
        ]
      : userFilters;

  const userFilterTileOptions = orderedUserFilters.map((user) => ({
    label: user.userId === userID ? `${user.name} (Me)` : user.name,
    value: user.userId,
  }));

  const findValuesFilter = (name: string) =>
    pHRequest?.filters?.find((filter) => filter.name === name) as
      | IPHValuesFilter
      | undefined;

  const findRangeFilter = (name: string) =>
    pHRequest?.filters?.find((filter) => filter.name === name) as
      | IPHRangeFilter
      | undefined;

  const isPOJobAndPaymentFilterEnabled =
    FeatureSwitches.enablePOJOBAndPaymentSearch === true;

  const isTotalAmountAndUserFilterEnabled =
    FeatureSwitches.enableTotalAmountAndUserCustomFilters === true;

  // True exactly when the new multi-select User picker is the one rendering
  // (matches the JSX branch below) — the condition under which the legacy
  // Everyone/Just Me userView concept is retired in favor of the userId
  // filter list.
  const isNewUserPickerActive =
    isTotalAmountAndUserFilterEnabled && permissionRulesCheck;

  // Each filter type's local state only seeds from a previously-applied
  // pHRequest.filters entry when that filter's own section is actually
  // rendered. Otherwise a stale filter from before a flag flip would stay
  // selected (and keep getting resubmitted) with no visible UI to change or
  // clear it.
  const [poJobNameFilter, setPOJobNameFilter] = useState<string[]>(
    isPOJobAndPaymentFilterEnabled
      ? findValuesFilter('poJobName')?.values ?? []
      : [],
  );

  const [paymentFilter, setPaymentFilter] = useState<string[]>(
    isPOJobAndPaymentFilterEnabled
      ? findValuesFilter('payment')?.values ?? []
      : [],
  );

  const [userFilter, setUserFilter] = useState<string[]>(
    isNewUserPickerActive ? findValuesFilter('userId')?.values ?? [] : [],
  );

  const grossSaleAmountFilter = isTotalAmountAndUserFilterEnabled
    ? findRangeFilter('grossSaleAmount')
    : undefined;
  const parsedMinAmount = grossSaleAmountFilter
    ? Number(grossSaleAmountFilter.minValue)
    : null;
  const parsedMaxAmount = grossSaleAmountFilter
    ? Number(grossSaleAmountFilter.maxValue)
    : null;
  // A malformed persisted range (either side fails to parse) isn't a
  // meaningful state to pre-fill — treat it as if no filter were applied
  // rather than showing a partially-broken "NaN to 75" range.
  const isValidPersistedRange =
    parsedMinAmount !== null &&
    parsedMaxAmount !== null &&
    !Number.isNaN(parsedMinAmount) &&
    !Number.isNaN(parsedMaxAmount);
  const initialMinAmount = isValidPersistedRange ? parsedMinAmount : null;
  const initialMaxAmount = isValidPersistedRange ? parsedMaxAmount : null;

  // appliedMinAmount/appliedMaxAmount are the last values committed via the
  // Apply button — the request and chip label are always built from these,
  // never from the live, not-yet-validated minAmount/maxAmount inputs below.
  const [appliedMinAmount, setAppliedMinAmount] = useState<null | number>(
    initialMinAmount,
  );
  const [appliedMaxAmount, setAppliedMaxAmount] = useState<null | number>(
    initialMaxAmount,
  );
  const totalAmountFilter =
    appliedMinAmount !== null && appliedMaxAmount !== null
      ? `$${appliedMinAmount} to $${appliedMaxAmount}`
      : '';

  const [minAmount, setMinAmount] = useState<null | number>(initialMinAmount);
  const [maxAmount, setMaxAmount] = useState<null | number>(initialMaxAmount);
  const [minAmountError, setMinAmountError] = useState('');
  const [maxAmountError, setMaxAmountError] = useState('');

  const resetTotalAmountFilter = () => {
    setAppliedMinAmount(null);
    setAppliedMaxAmount(null);
    setMinAmount(null);
    setMaxAmount(null);
    setMinAmountError('');
    setMaxAmountError('');
  };

  const [filterSettings, setFilterSettings] = useState<UserFilterSettings>({
    userView: isNewUserPickerActive
      ? 'allOrders'
      : pHRequest?.resultsFilter || 'allOrders',
  });

  const [isFilterChanged, setIsFilterChanged] = useState(false);

  const closeFilterDrawer = () => {
    setIsDrawerOpen(false);
  };

  const filtersMatch = (previousFilter: IPHFilter, newFilter: IPHFilter) => {
    if ('values' in previousFilter && 'values' in newFilter) {
      return (
        previousFilter.values.length === newFilter.values.length &&
        previousFilter.values.every((val) => newFilter.values.includes(val))
      );
    }
    if ('minValue' in previousFilter && 'minValue' in newFilter) {
      return (
        previousFilter.minValue === newFilter.minValue &&
        previousFilter.maxValue === newFilter.maxValue
      );
    }
    return false;
  };

  const hasFilterChanges = (
    previousFilters: IPHFilter[] = [],
    newFilters: IPHFilter[] = [],
  ) => {
    if (previousFilters.length !== newFilters.length) {
      return true;
    }

    return previousFilters.some((filter) => {
      const newFilter = newFilters.find((fil) => fil.name === filter.name);
      if (!newFilter) return true;
      return !filtersMatch(filter, newFilter);
    });
  };

  const getFilters = () => {
    const filters: IPHFilter[] = [];
    if (isPOJobAndPaymentFilterEnabled && poJobNameFilter.length > 0) {
      filters.push({ name: 'poJobName', values: poJobNameFilter });
    }
    if (isPOJobAndPaymentFilterEnabled && paymentFilter.length > 0) {
      filters.push({ name: 'payment', values: paymentFilter });
    }
    if (
      isTotalAmountAndUserFilterEnabled &&
      appliedMinAmount !== null &&
      appliedMaxAmount !== null
    ) {
      filters.push({
        name: 'grossSaleAmount',
        minValue: appliedMinAmount.toFixed(2),
        maxValue: appliedMaxAmount.toFixed(2),
      });
    }
    if (isNewUserPickerActive && userFilter.length > 0) {
      filters.push({ name: 'userId', values: userFilter });
    }
    return filters;
  };

  const handleUserViewChange = () => {
    setPHRequest((prevState: IPHRequest) => {
      const shouldRecomputeFilters =
        isPOJobAndPaymentFilterEnabled || isTotalAmountAndUserFilterEnabled;
      const newFilters = shouldRecomputeFilters
        ? getFilters()
        : prevState.filters;
      const hasChangedFilters =
        shouldRecomputeFilters &&
        hasFilterChanges(prevState.filters ?? [], newFilters ?? []);
      const nextResultsFilter = filterSettings.userView ?? undefined;

      if (
        prevState.resultsFilter === nextResultsFilter &&
        prevState.pageNumber === 1 &&
        !hasChangedFilters
      ) {
        return prevState;
      }

      return {
        ...prevState,
        resultsFilter: nextResultsFilter,
        pageNumber: 1,
        filters: hasChangedFilters ? newFilters : prevState.filters,
      };
    });
    setPHFilterToSession(filterSettings);
    closeFilterDrawer();
    setIsFilterChanged(false);
  };

  return (
    <Drawer
      open={isDrawerOpen}
      onClose={closeFilterDrawer}
      style={{ zIndex: 20000000010 }}
    >
      <DrawerHeader title="Filter" onClose={closeFilterDrawer} />
      <DrawerBody>
        <>
          {permissionRulesCheck &&
            (isTotalAmountAndUserFilterEnabled ? (
              <div className="sui-border-b-1 sui-border-solid sui-border-strong">
                <OrderFilterGroup
                  title="User"
                  tileOptions={userFilterTileOptions}
                  selectedOptions={userFilter}
                  setSelectedOptions={setUserFilter}
                  setFilterChanged={setIsFilterChanged}
                />
              </div>
            ) : (
              <OrderFilterUserSection
                showDot={dateFilterDot.user}
                userView={filterSettings.userView || 'allOrders'}
                username={username}
                setFilterSettings={setFilterSettings}
              />
            ))}
          {isPOJobAndPaymentFilterEnabled && (
            <>
              <div className="sui-border-b-1 sui-border-solid sui-border-strong">
                <OrderFilterGroup
                  title="PO/Job Name"
                  tileOptions={poJobNameTileOptions}
                  selectedOptions={poJobNameFilter}
                  setSelectedOptions={setPOJobNameFilter}
                  setFilterChanged={setIsFilterChanged}
                />
              </div>
              <div className="sui-border-b-1 sui-border-solid sui-border-strong">
                <OrderFilterGroup
                  title="Payment"
                  tileOptions={paymentTileOptions}
                  selectedOptions={paymentFilter}
                  setSelectedOptions={setPaymentFilter}
                  setFilterChanged={setIsFilterChanged}
                />
              </div>
            </>
          )}

          {isTotalAmountAndUserFilterEnabled && (
            <div className="sui-border-b-1 sui-border-solid sui-border-strong">
              <OrderTotalAmountFilter
                minAmount={minAmount}
                maxAmount={maxAmount}
                setMinAmount={setMinAmount}
                setMaxAmount={setMaxAmount}
                minAmountError={minAmountError}
                maxAmountError={maxAmountError}
                setMinAmountError={setMinAmountError}
                setMaxAmountError={setMaxAmountError}
                setAppliedMinAmount={setAppliedMinAmount}
                setAppliedMaxAmount={setAppliedMaxAmount}
                setFilterChanged={setIsFilterChanged}
              />
            </div>
          )}
        </>
      </DrawerBody>
      <OrderFilterFooter
        filterSettings={filterSettings}
        pHRequestResultsFilter={pHRequest.resultsFilter}
        permissionRulesCheck={permissionRulesCheck}
        username={username}
        poJobNameFilter={poJobNameFilter}
        paymentFilter={paymentFilter}
        totalAmountFilter={totalAmountFilter}
        userFilter={userFilter}
        userFilterTileOptions={userFilterTileOptions}
        setFilterSettings={setFilterSettings}
        setPOJobNameFilter={setPOJobNameFilter}
        setPaymentFilter={setPaymentFilter}
        setUserFilter={setUserFilter}
        resetTotalAmountFilter={resetTotalAmountFilter}
        onViewResults={() => {
          const previousUserIds = findValuesFilter('userId')?.values ?? [];
          const userFilterChanged =
            previousUserIds.length !== userFilter.length ||
            previousUserIds.some((val) => !userFilter.includes(val));
          const legacyUserViewChanged =
            filterSettings.userView !== pHRequest.resultsFilter;

          if (
            isNewUserPickerActive ? userFilterChanged : legacyUserViewChanged
          ) {
            setDateFilterDot((prevState) => ({ ...prevState, user: true }));
          }
          handleUserViewChange();

          // A policy-restricted user's userView is forced to 'myOrders' with
          // no UI to change it — that's not a choice, so it shouldn't count
          // as one.
          const legacyUserChosenCount =
            permissionRulesCheck && filterSettings.userView !== 'allOrders'
              ? 1
              : 0;
          const userChosenCount = isNewUserPickerActive
            ? userFilter.length
            : legacyUserChosenCount;
          pushEventInfo({
            category: { primaryCategory: 'button submission' },
            eventInfo: { eventName: 'my account settings' },
            myAccount: {
              settings: `purchase history: search filters: user ${userChosenCount}: pojobname ${poJobNameFilter.length}: payments ${paymentFilter.length}`,
            },
          });
        }}
        isFilterChanged={isFilterChanged}
        setFilterChanged={setIsFilterChanged}
      />
    </Drawer>
  );
};
